import { useState, type CSSProperties } from 'react';

import { useI18n } from '../lib/use-i18n.js';
import { logger } from '../lib/logger.js';
import { callExportReport } from '../lib/callables/reports.js';

/**
 * Download the full range as a spreadsheet (gap G9).
 *
 * The export runs on the server, and that is the entire point: the dashboard
 * reads daily stats through a bounded listener, so a client-side export could
 * only ever save the rows already on screen. An operator asking for a year got
 * a truncated file with nothing on it saying so — worse than no export, because
 * they would have reconciled against it.
 *
 * Every state says what happened. "Nothing to export in this range" is a real
 * answer, not a failure, and it is better than handing someone a file with a
 * header and no rows and letting them wonder.
 */

type State =
  | { readonly phase: 'idle' }
  | { readonly phase: 'working' }
  | { readonly phase: 'done'; readonly rows: number }
  | { readonly phase: 'empty' }
  | { readonly phase: 'error' };

interface ReportExportButtonProps {
  readonly tenantId: string;
  readonly outletId: string;
  /** Inclusive SGT day keys for the range currently on screen. */
  readonly from: string;
  readonly to: string;
}

export function ReportExportButton({ tenantId, outletId, from, to }: ReportExportButtonProps) {
  const { t } = useI18n();
  const [state, setState] = useState<State>({ phase: 'idle' });

  async function run(): Promise<void> {
    setState({ phase: 'working' });
    try {
      const result = await callExportReport({ tenantId, outletId, from, to });

      if (result.rowCount === 0) {
        // Deliberately not a download: a file with a header and no rows invites
        // someone to think the business had no takings rather than that the
        // range was wrong.
        setState({ phase: 'empty' });
        return;
      }

      // Blob + object URL, revoked immediately after the click. Leaving it
      // alive holds the whole CSV in memory for the life of the tab.
      const blob = new Blob([result.csv], { type: result.contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);

      setState({ phase: 'done', rows: result.rowCount });
    } catch (e: unknown) {
      logger.error('report export failed', { error: String(e), tenantId, outletId, from, to });
      setState({ phase: 'error' });
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)', justifyItems: 'start' }}>
      <button
        type="button"
        style={buttonStyle}
        disabled={state.phase === 'working'}
        aria-busy={state.phase === 'working'}
        onClick={() => void run()}
      >
        {state.phase === 'working'
          ? t('dashboard.reports.exporting')
          : t('dashboard.reports.exportCta')}
      </button>

      <p style={hintStyle}>{t('dashboard.reports.exportHint')}</p>

      {state.phase === 'done' && (
        <p role="status" style={okStyle}>
          {t('dashboard.reports.exportDone', { rows: state.rows })}
        </p>
      )}
      {state.phase === 'empty' && (
        <p role="status" style={hintStyle}>
          {t('dashboard.reports.exportEmpty')}
        </p>
      )}
      {state.phase === 'error' && (
        <p role="alert" style={errStyle}>
          {t('dashboard.reports.exportFailed')}
        </p>
      )}
    </div>
  );
}

const buttonStyle: CSSProperties = {
  minHeight: 'var(--touch-target)',
  padding: '0 var(--space-5)',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-strong, var(--color-border))',
  background: 'transparent',
  color: 'var(--color-on-surface)',
  fontWeight: 600,
  fontSize: 'var(--text-body)',
  cursor: 'pointer',
};

const hintStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  maxWidth: '52ch',
};

const okStyle: CSSProperties = { margin: 0, fontSize: 'var(--text-meta)', fontWeight: 600 };

const errStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-warning, #b45309)',
};
